import { NAME_FORMAT, RENTAL_STATUS } from '@/constants/tenants';

export const resolveTenantName = (
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  fullName: string | null | undefined,
  nameFormat: string,
  valueForEmptyName: string,
): string => {
  if (!firstName && !lastName && !fullName) {
    return valueForEmptyName;
  }

  if (fullName) {
    const parts = fullName.trim().split(' ');
    if (parts.length >= 2) {
      firstName = firstName || parts[0];
      lastName = lastName || parts[parts.length - 1];
    } else {
      return fullName;
    }
  }

  if (!firstName && !lastName) {
    return valueForEmptyName;
  }

  switch (nameFormat) {
    case NAME_FORMAT.F_LAST:
      if (firstName && lastName) {
        return `${firstName.charAt(0)}. ${lastName}`;
      }
      return firstName || lastName || valueForEmptyName;

    case NAME_FORMAT.FIRST_LAST:
      if (firstName && lastName) {
        return `${firstName} ${lastName}`;
      }
      return firstName || lastName || valueForEmptyName;

    case NAME_FORMAT.L_FIRST:
      if (firstName && lastName) {
        return `${lastName.charAt(0)}. ${firstName}`;
      }
      return firstName || lastName || valueForEmptyName;

    default:
      if (firstName && lastName) {
        return `${firstName.charAt(0)}. ${lastName}`;
      }
      return firstName || lastName || valueForEmptyName;
  }
};

export const isApartmentRented = (apartment: any): boolean => {
  return apartment.rentalStatus?.toLowerCase() === RENTAL_STATUS.RENTED;
};

export const getContractDates = (apartment: any, now: Date, futureThreshold?: Date) => {
  const contractFromDate = apartment.contractFromDate ? new Date(apartment.contractFromDate) : null;
  const contractToDate = apartment.contractToDate ? new Date(apartment.contractToDate) : null;

  const hasStarted = !contractFromDate || contractFromDate <= now;
  const hasNotEnded = !contractToDate || contractToDate >= now;
  const isInFutureStartDateRange =
    futureThreshold && contractFromDate
      ? contractFromDate > now && contractFromDate <= futureThreshold
      : false;

  return {
    contractFromDate,
    contractToDate,
    hasStarted,
    hasNotEnded,
    isInContractRange: hasStarted && hasNotEnded,
    isInFutureStartDateRange,
  };
};

export const getVacantApartment = (apartment: any) => {
  return {
    apartmentNumber: apartment.apartmentNumber,
    apartmentId: apartment.apartmentId,
    externalId: apartment.externalId,
    streetAddress: apartment.streetAddress || null,
    postalCode: apartment.postalCode || null,
    postalAddressCity: apartment.postalAddressCity || null,
    rentalStatus: RENTAL_STATUS.VACANT,
    contractId: null,
    contractFromDate: null,
    contractToDate: null,
    stairwellNames: [],
    names: [],
    customNames: [],
    tenant1FirstName: null,
    tenant1LastName: null,
    tenant1FullName: null,
    tenant2FirstName: null,
    tenant2LastName: null,
    tenant2FullName: null,
    visible: apartment.visible !== undefined ? apartment.visible : true,
  };
};

export const categorizeApartmentsByContract = (
  apartmentGroup: any[],
  now: Date,
  futureThreshold?: Date,
) => {
  let currentApartment = null;

  for (const apartment of apartmentGroup) {
    const { hasStarted, hasNotEnded, isInContractRange, isInFutureStartDateRange } =
      getContractDates(apartment, now, futureThreshold);

    if (isInContractRange) {
      if (!currentApartment) {
        currentApartment = apartment;
      } else if (isApartmentRented(apartment) && !isApartmentRented(currentApartment)) {
        currentApartment = apartment;
      }

      continue;
    }

    const isFutureApartment = !hasStarted && hasNotEnded;

    if (isFutureApartment) {
      const isFutureApartmentSuitableForCurrent =
        isApartmentRented(apartment) && isInFutureStartDateRange;

      const isCurrentApartmentNotRented =
        !currentApartment || !isApartmentRented(currentApartment);

      if (isFutureApartmentSuitableForCurrent && isCurrentApartmentNotRented) {
        currentApartment = apartment;
      }
    }
  }

  return {
    currentApartment,
  };
};

export const filterApartmentsByContract = (
  apartments: any[],
  contractDays: number,
  showAllApartments: boolean,
  apartmentOrder?: { [apartmentId: string]: number },
) => {
  if (!apartments || apartments.length === 0) {
    return apartments;
  }

  const now = new Date();
  const futureThreshold = new Date(now.getTime() + contractDays * 24 * 60 * 60 * 1000);

  // Group contracts by apartment, the sync API can return several contracts per apartment
  const apartmentGroups: { [key: string]: any[] } = {};

  apartments.forEach((apartment) => {
    const key = apartment.externalId || apartment.apartmentNumber;

    if (!apartmentGroups[key]) {
      apartmentGroups[key] = [];
    }

    apartmentGroups[key].push(apartment);
  });

  const validApartments: any[] = [];

  Object.values(apartmentGroups).forEach((apartmentGroup) => {
    const { currentApartment } = categorizeApartmentsByContract(
      apartmentGroup,
      now,
      futureThreshold,
    );

    if (showAllApartments) {
      validApartments.push(currentApartment || getVacantApartment(apartmentGroup[0]));
      return;
    }

    if (currentApartment && isApartmentRented(currentApartment)) {
      validApartments.push(currentApartment);
    }
  });

  if (apartmentOrder && Object.keys(apartmentOrder).length > 0) {
    return validApartments.sort((a, b) => {
      const orderA = apartmentOrder[a.apartmentId];
      const orderB = apartmentOrder[b.apartmentId];

      if (orderA === undefined) return 1;
      if (orderB === undefined) return -1;
      return orderA - orderB;
    });
  }

  return sortApartmentsByNumber(validApartments);
};

export const sortApartmentsByNumber = (apartments: any[]) => {
  return apartments.sort((a, b) => {
    const apartmentA = a.apartmentNumber || '';
    const apartmentB = b.apartmentNumber || '';

    return apartmentA.localeCompare(apartmentB, undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  });
};
